/**
 * MessageThread Component
 *
 * Scrollable container for chat messages with auto-scroll to bottom.
 * Displays user and assistant messages with role-based styling.
 */

import {
  Component,
  For,
  Show,
  Accessor,
  createEffect,
  createSignal,
  onMount,
} from "solid-js";
import { renderChatMarkdown } from "../../utils/chatMarkdown";
import { renderMarkdown } from "../../utils/markdown";
import { decorateReferences } from "../../utils/decorateReferences";
import { handleCopyCodeBlockClick } from "../../utils/copyCodeBlock";
import { logoSrc } from "../../stores/themeStore";
import {
  useEntityReferences,
  type ResolvedReference,
} from "../../hooks/useEntityReferences";
import { Avatar } from "../ui/Avatar";
import { Spinner } from "../ui/Spinner";
import { Dropdown, DropdownItem } from "../ui/Dropdown";
import { Modal } from "../ui/Modal";
import type {
  Message,
  Proposal,
  ProposalId,
  ProposedActionId,
  MessageId,
  ChatViewContext,
} from "../../api/types";
import type { ProposalOperationResult } from "../../hooks/useProposals";
import { InlineProposal } from "./InlineProposal";
import { ReportIssueModal } from "./ReportIssueModal";
import { ReferencePreviewModal } from "./ReferencePreviewModal";

export const CONTINUE_PROMPT = "Continue where you left off.";

export interface MessageThreadProps {
  /** Messages to display */
  messages: Message[];
  /** Whether AI is currently responding */
  isResponding?: boolean;
  /** Streaming text content being received from AI */
  streamingContent?: string;
  /** Send a follow-up turn on the user's behalf (e.g. Continue after a tool-use limit) */
  onSendFollowUp?: (content: string) => Promise<void>;
  /** Callback to approve a proposal (from useProposals) */
  onApproveProposal?: (
    id: ProposalId,
    actionIds?: ProposedActionId[],
  ) => Promise<ProposalOperationResult>;
  /** Callback to reject a proposal (from useProposals) */
  onRejectProposal?: (id: ProposalId) => Promise<ProposalOperationResult>;
  /** Callback after any proposal action (for refetching) */
  onProposalActioned?: () => void;
  /** Look up the hydrated proposal for a message's inline card */
  getProposal?: (id: ProposalId) => Proposal | undefined;
  /** Current user's display name (for avatar) */
  userName?: string;
  /** Current user's avatar image URL */
  userAvatarUrl?: string;
  /** Current view context (passed through to re-send after edit) */
  viewContext?: ChatViewContext;
  /** Delete message and all subsequent; re-send with newContent if provided */
  onDeleteMessageAndResend?: (
    messageId: MessageId,
    resendContent?: string,
    viewContext?: ChatViewContext,
  ) => Promise<void>;
  /** When set, the message with this ID should auto-enter edit mode */
  editTargetMessageId?: Accessor<string | null>;
  /** Called after the edit target has been consumed to clear the signal */
  onEditTargetConsumed?: () => void;
  /** Index of the message that was copied via Ctrl/Cmd+C — shows checkmark feedback */
  copiedMsgIdx?: Accessor<number>;
  /** Retry sending a failed message */
  onRetryMessage?: (messageId: MessageId) => Promise<void>;
  /** Project whose work items and documents chat references resolve against */
  projectId?: string;
  /** Project display-ID prefix, e.g. "FLOW". References are inert without it. */
  projectPrefix?: string;
}

interface ErrorIndicatorProps {
  message: Message;
  onRetry: (messageId: MessageId) => Promise<void>;
}

const ErrorIndicator: Component<ErrorIndicatorProps> = (props) => {
  const [isRetrying, setIsRetrying] = createSignal(false);

  const handleRetry = async () => {
    setIsRetrying(true);
    try {
      await props.onRetry(props.message.id);
    } catch {
      // Swallowed: the error already renders in this indicator (FLOW-692)
    } finally {
      setIsRetrying(false);
    }
  };

  const errorMessage = () =>
    props.message._clientError?.error.message || "Failed to send message";

  return (
    <div class="flex items-center gap-2 mt-2 p-2 bg-signal-red-tint border border-signal-red/25 rounded text-sm text-signal-red">
      <svg
        class="w-4 h-4 flex-shrink-0"
        fill="currentColor"
        viewBox="0 0 20 20"
        aria-hidden="true"
      >
        <path
          fill-rule="evenodd"
          d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
          clip-rule="evenodd"
        />
      </svg>
      <div class="flex-1">
        <p>{errorMessage()}</p>
      </div>
      <button
        type="button"
        onClick={handleRetry}
        disabled={isRetrying()}
        class="px-3 py-1 bg-transparent text-signal-red border border-signal-red rounded hover:bg-surface text-xs font-medium transition-colors"
        aria-label="Retry sending message"
      >
        {isRetrying() ? "Retrying..." : "Retry"}
      </button>
    </div>
  );
};

interface RegenerateButtonProps {
  onClick: () => void;
  disabled: boolean;
  class: string;
}

// Shared by the red (xml_tool_call_leak etc.) and amber (tool_loop_exhausted)
// error branches below — one render path for the icon/label/handler contract
// so the two error treatments cannot drift apart on this button.
const RegenerateButton: Component<RegenerateButtonProps> = (props) => {
  return (
    <button
      type="button"
      class={props.class}
      onClick={props.onClick}
      disabled={props.disabled}
      aria-label="Regenerate response"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="12"
        height="12"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2.5"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true"
      >
        <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
        <path d="M3 3v5h5" />
      </svg>
      Regenerate
    </button>
  );
};

export const MessageThread: Component<MessageThreadProps> = (props) => {
  let containerRef: HTMLDivElement | undefined;

  // One resolver per thread: every persisted message body is scanned and the
  // references across all of them resolve in a single batched request.
  const references = useEntityReferences(
    () => props.projectId,
    () => props.projectPrefix,
  );

  // Assistant messages only: a user message renders as a plain `<pre>` and
  // grows no pills, so scanning one buys resolve requests for references
  // nothing will ever display.
  //
  // Deliberately does not read `streamingContent`: a half-typed "FLOW-1" is a
  // different key from the "FLOW-123" it becomes, so tracking the stream would
  // fire a request per chunk, poison `attempted` with items the message never
  // referenced, and re-parse every message body on each chunk. Pills resolve
  // the moment the reply lands in `messages`.
  createEffect(() => {
    references.track(
      props.messages
        .filter((message) => message.role === "assistant")
        .map((message) => message.content),
    );
  });

  const [activeRefKey, setActiveRefKey] = createSignal<string | null>(null);

  // A pill that has not resolved has nothing to preview, so it stays inert
  // rather than queueing a modal that pops open when resolution lands.
  const openReference = (key: string) => {
    if (!references.lookup(key)) return;
    setActiveRefKey(key);
  };

  const activeReference = (): ResolvedReference | undefined => {
    references.version();
    const key = activeRefKey();
    return key ? references.lookup(key) : undefined;
  };

  // Auto-scroll to bottom when messages change or streaming content updates
  const scrollToBottom = () => {
    if (containerRef) {
      const reducedMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)",
      ).matches;
      containerRef.scrollTo({
        top: containerRef.scrollHeight,
        behavior: reducedMotion ? "auto" : "smooth",
      });
    }
  };

  onMount(scrollToBottom);

  createEffect(() => {
    // Track dependencies by accessing them
    const _msgCount = props.messages.length;
    const _isResponding = props.isResponding;
    const _streamLen = props.streamingContent?.length ?? 0;
    // Scroll to bottom when messages change, AI starts responding, or streaming updates
    if (_msgCount >= 0 || _isResponding || _streamLen > 0) {
      scrollToBottom();
    }
  });

  return (
    <div ref={containerRef} class="flex-1 overflow-y-auto p-4 space-y-4">
      <For each={props.messages}>
        {(message, index) => {
          const precedingUserMessage = () => {
            const msgs = props.messages;
            for (let i = index() - 1; i >= 0; i--) {
              if (msgs[i].role === "user") return msgs[i];
            }
            return undefined;
          };
          const isEditTarget = () =>
            props.editTargetMessageId?.() === message.id;
          const isExternalCopied = () => props.copiedMsgIdx?.() === index();
          return (
            <div class="message-enter">
              <MessageBubble
                message={message}
                index={index()}
                isLast={index() === props.messages.length - 1}
                isResponding={props.isResponding}
                onSendFollowUp={props.onSendFollowUp}
                onApproveProposal={props.onApproveProposal}
                onRejectProposal={props.onRejectProposal}
                onProposalActioned={props.onProposalActioned}
                getProposal={props.getProposal}
                userName={props.userName}
                userAvatarUrl={props.userAvatarUrl}
                viewContext={props.viewContext}
                onDeleteMessageAndResend={props.onDeleteMessageAndResend}
                precedingUserMessage={precedingUserMessage()}
                autoEdit={isEditTarget()}
                onAutoEditConsumed={props.onEditTargetConsumed}
                externalCopied={isExternalCopied()}
                onRetryMessage={props.onRetryMessage}
                projectPrefix={props.projectPrefix}
                lookupReference={references.lookup}
                referenceVersion={references.version}
                onOpenReference={openReference}
              />
            </div>
          );
        }}
      </For>

      {/* Streaming / typing indicator - shown when AI is responding */}
      <Show when={props.isResponding}>
        <div class="flex gap-3">
          <Avatar
            name="AI"
            src={logoSrc()}
            size="sm"
            class="flex-shrink-0 bg-signal-magenta-tint text-signal-magenta"
          />
          <Show
            when={props.streamingContent}
            fallback={
              <div class="bg-surface border border-line rounded rounded-tl-none p-3">
                <div class="flex items-center gap-2 text-sm text-ink-3">
                  <div class="typing-dots" aria-hidden="true">
                    <span />
                    <span />
                    <span />
                  </div>
                  <span>AI is thinking...</span>
                </div>
              </div>
            }
          >
            <div class="flex-1 min-w-0">
              <div class="inline-block max-w-[85%] rounded p-3 bg-surface border border-line rounded-tl-none">
                {/* Plain markdown while streaming: reference pills would be
                    inert — unresolved, and keyboard-focusable with nothing to
                    activate — until the reply is persisted. */}
                <div
                  class="chat-markdown text-sm text-ink-2"
                  innerHTML={renderMarkdown(props.streamingContent ?? "")}
                />
                <div class="flex items-center gap-1 mt-1">
                  <Spinner size="sm" />
                </div>
              </div>
            </div>
          </Show>
        </div>
      </Show>

      <ReferencePreviewModal
        reference={activeReference()}
        open={activeRefKey() !== null}
        onClose={() => setActiveRefKey(null)}
      />
    </div>
  );
};

interface MessageBubbleProps {
  message: Message;
  /** Index of this message in the list (for keyboard navigation) */
  index: number;
  /** Whether this is the last message in the thread (gates the Continue action) */
  isLast?: boolean;
  /** Whether AI is currently responding (disables Continue while streaming) */
  isResponding?: boolean;
  /** Send a follow-up turn on the user's behalf (e.g. Continue after a tool-use limit) */
  onSendFollowUp?: (content: string) => Promise<void>;
  onApproveProposal?: (
    id: ProposalId,
    actionIds?: ProposedActionId[],
  ) => Promise<ProposalOperationResult>;
  onRejectProposal?: (id: ProposalId) => Promise<ProposalOperationResult>;
  onProposalActioned?: () => void;
  getProposal?: (id: ProposalId) => Proposal | undefined;
  userName?: string;
  userAvatarUrl?: string;
  viewContext?: ChatViewContext;
  onDeleteMessageAndResend?: (
    messageId: MessageId,
    resendContent?: string,
    viewContext?: ChatViewContext,
  ) => Promise<void>;
  /** Nearest preceding user message (used for regenerate — delete it and re-send its content) */
  precedingUserMessage?: Message;
  /** When true, auto-enter edit mode (triggered by ↑ shortcut) */
  autoEdit?: boolean;
  /** Called after auto-edit has been triggered to reset the signal */
  onAutoEditConsumed?: () => void;
  /** When true, show the copied checkmark (triggered by Ctrl/Cmd+C shortcut) */
  externalCopied?: boolean;
  /** Retry sending a failed message */
  onRetryMessage?: (messageId: MessageId) => Promise<void>;
  /** Project display-ID prefix used to linkify references in this message */
  projectPrefix?: string;
  /** Resolved reference lookup, keyed by a pill's data-ref-key */
  lookupReference?: (key: string) => ResolvedReference | undefined;
  /** Bumps when new resolutions land, re-running pill decoration */
  referenceVersion?: Accessor<number>;
  /** Called with the data-ref-key of an activated reference pill */
  onOpenReference?: (key: string) => void;
}

const MessageBubble: Component<MessageBubbleProps> = (props) => {
  const isUser = () => props.message.role === "user";
  const isAssistant = () => props.message.role === "assistant";
  const isExhausted = () => props.message.error_kind === "tool_loop_exhausted";
  const [copied, setCopied] = createSignal(false);
  const [editMode, setEditMode] = createSignal(false);
  const [editText, setEditText] = createSignal("");
  const [confirmDelete, setConfirmDelete] = createSignal(false);
  const [reportIssueOpen, setReportIssueOpen] = createSignal(false);
  let editTextareaRef: HTMLTextAreaElement | undefined;
  let contentRef: HTMLDivElement | undefined;

  // Pills render immediately from the markdown and fill in once the thread's
  // batched resolution lands; re-run on every version bump and on any change
  // that rebuilds the bubble's innerHTML — the message body, and the project
  // prefix, which arrives after the projects resource settles.
  createEffect(() => {
    props.referenceVersion?.();
    void props.message.content;
    void props.projectPrefix;
    const lookup = props.lookupReference;
    if (contentRef && lookup) decorateReferences(contentRef, lookup);
  });

  // Merge external copy signal (from Ctrl/Cmd+C shortcut) with local copy state
  const isCopied = () => copied() || (props.externalCopied ?? false);

  // Auto-enter edit mode when triggered by keyboard shortcut
  createEffect(() => {
    if (props.autoEdit && isUser() && !editMode()) {
      props.onAutoEditConsumed?.();
      enterEditMode();
    }
  });

  const copyMessageContent = async (e: MouseEvent) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(props.message.content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API not available — silent fail
    }
  };

  /** The reference pill an event originated in, if any. */
  const pillFor = (target: EventTarget | null): HTMLElement | null =>
    (target as HTMLElement | null)?.closest?.("a.entity-ref[data-ref-key]") ??
    null;

  const activatePill = (pill: HTMLElement) => {
    props.onOpenReference?.(pill.getAttribute("data-ref-key") ?? "");
  };

  // Enter/Space for the href-less pills, which are anchors without a target
  // and so get no activation from the browser.
  const handleContentKeyDown = (e: KeyboardEvent) => {
    if (e.key !== "Enter" && e.key !== " ") return;
    const pill = pillFor(e.target);
    if (!pill) return;
    e.preventDefault();
    activatePill(pill);
  };

  // Event delegation for reference pills and code block copy buttons (both
  // rendered via innerHTML)
  const handleContentClick = (e: MouseEvent) => {
    const pill = pillFor(e.target);
    if (pill) {
      e.preventDefault();
      e.stopPropagation();
      activatePill(pill);
      return;
    }
    if (!handleCopyCodeBlockClick(e.target)) return;
    e.preventDefault();
    e.stopPropagation();
  };

  const enterEditMode = () => {
    setEditText(props.message.content);
    setEditMode(true);
    requestAnimationFrame(() => {
      if (editTextareaRef) {
        editTextareaRef.focus();
        editTextareaRef.select();
      }
    });
  };

  const cancelEdit = () => {
    setEditMode(false);
    setEditText("");
  };

  const submitEdit = async () => {
    const content = editText().trim();
    if (!content) return;
    setEditMode(false);
    await props.onDeleteMessageAndResend?.(
      props.message.id,
      content,
      props.viewContext,
    );
  };

  const handleEditKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      e.preventDefault();
      cancelEdit();
    } else if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void submitEdit();
    }
  };

  const handleDelete = async () => {
    setConfirmDelete(false);
    await props.onDeleteMessageAndResend?.(props.message.id);
  };

  const handleRegenerate = async () => {
    const userMsg = props.precedingUserMessage;
    if (!userMsg) return;
    await props.onDeleteMessageAndResend?.(
      userMsg.id,
      userMsg.content,
      props.viewContext,
    );
  };

  const actionsBtnClass = () =>
    "msg-actions-btn p-1 rounded transition-all " +
    "opacity-0 group-hover/msg:opacity-100 focus-visible:opacity-100 max-sm:opacity-100 " +
    (isUser()
      ? "text-paper/70 hover:text-paper hover:bg-ink-2"
      : "text-ink-3 hover:text-ink hover:bg-sunken");

  // Shared by the amber row's Continue button and its RegenerateButton call
  // site — both are amber actions on the same tool_loop_exhausted row.
  const amberActionClass =
    "flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded " +
    "bg-signal-amber-tint text-signal-amber border border-signal-amber/25 " +
    "hover:text-ink transition-colors focus-ring";

  return (
    <div
      class={`flex gap-3 rounded focus-ring ${isUser() ? "flex-row-reverse" : ""}`}
      data-msg-idx={props.index}
      tabindex="-1"
      aria-label={`${isUser() ? "Your" : "AI"} message`}
    >
      {/* Avatar */}
      <Show when={isAssistant()}>
        <Avatar
          name="AI"
          src={logoSrc()}
          size="sm"
          class="flex-shrink-0 bg-signal-magenta-tint text-signal-magenta"
        />
      </Show>
      <Show when={isUser()}>
        <Avatar
          name={props.userName ?? "You"}
          src={props.userAvatarUrl}
          size="sm"
          class="shrink-0"
        />
      </Show>

      {/* Message content */}
      <div class={`flex-1 min-w-0 ${isUser() ? "text-right" : ""}`}>
        <div
          class={
            "group/msg relative inline-block max-w-[85%] rounded p-3 text-left transition-colors " +
            (isUser()
              ? "bg-ink hover:bg-ink-2 text-paper rounded-tr-none"
              : "bg-surface hover:bg-sunken border border-line rounded-tl-none")
          }
        >
          {/* Action buttons row — copy + kebab menu */}
          <div class="absolute top-1.5 right-1.5 flex items-center gap-0.5">
            {/* Copy button */}
            <button
              type="button"
              class={actionsBtnClass() + (isCopied() ? " !opacity-100" : "")}
              onClick={copyMessageContent}
              aria-label="Copy message"
              title="Copy message"
            >
              <Show
                when={isCopied()}
                fallback={
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                  </svg>
                }
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  class="text-signal-green"
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </Show>
            </button>

            {/* Kebab actions menu — only when handler is wired up */}
            <Show when={props.onDeleteMessageAndResend}>
              <Dropdown
                align="right"
                trigger={
                  <button
                    type="button"
                    class={actionsBtnClass()}
                    aria-label="Message actions"
                    title="Message actions"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <circle cx="12" cy="5" r="1.5" />
                      <circle cx="12" cy="12" r="1.5" />
                      <circle cx="12" cy="19" r="1.5" />
                    </svg>
                  </button>
                }
              >
                <Show when={isUser()}>
                  <DropdownItem onClick={enterEditMode}>Edit</DropdownItem>
                </Show>
                <Show when={isAssistant()}>
                  <DropdownItem onClick={handleRegenerate}>
                    Regenerate
                  </DropdownItem>
                  <DropdownItem
                    onClick={() => {
                      if (props.message.correlation_id) {
                        setReportIssueOpen(true);
                      }
                    }}
                    disabled={!props.message.correlation_id}
                    title={
                      props.message.correlation_id
                        ? undefined
                        : "No trace available"
                    }
                  >
                    Report an Issue
                  </DropdownItem>
                </Show>
                <DropdownItem
                  class="text-signal-red hover:bg-signal-red-tint focus:bg-signal-red-tint"
                  onClick={() => setConfirmDelete(true)}
                >
                  Delete
                </DropdownItem>
              </Dropdown>
            </Show>
          </div>

          {/* Message body — normal view or inline edit */}
          <Show
            when={editMode()}
            fallback={
              <>
                <Show
                  when={isAssistant()}
                  fallback={
                    <pre class="text-sm whitespace-pre-wrap font-sans text-paper pr-14">
                      {props.message.content}
                    </pre>
                  }
                >
                  <Show
                    when={props.message.error_kind && !isExhausted()}
                    fallback={
                      <>
                        <div
                          ref={contentRef}
                          class="chat-markdown text-sm text-ink-2 pr-14"
                          innerHTML={renderChatMarkdown(
                            props.message.content,
                            props.projectPrefix ?? "",
                          )}
                          onClick={handleContentClick}
                          onKeyDown={handleContentKeyDown}
                        />
                        <Show when={isExhausted()}>
                          <div
                            role="group"
                            aria-label="Response stopped early"
                            class="flex items-center gap-2 flex-wrap rounded border border-signal-amber/25 bg-signal-amber-tint px-3 py-2 mt-2"
                          >
                            <span class="text-xs text-signal-amber">
                              Stopped early — tool-use limit reached.
                            </span>
                            <Show when={props.isLast && props.onSendFollowUp}>
                              <button
                                type="button"
                                class={amberActionClass}
                                onClick={() =>
                                  props.onSendFollowUp?.(CONTINUE_PROMPT)
                                }
                                disabled={props.isResponding}
                                aria-label="Continue where you left off"
                              >
                                Continue
                              </button>
                            </Show>
                            <RegenerateButton
                              onClick={handleRegenerate}
                              disabled={!props.precedingUserMessage}
                              class={amberActionClass}
                            />
                          </div>
                        </Show>
                      </>
                    }
                  >
                    <div class="flex flex-col gap-2 pr-14">
                      <pre class="text-sm whitespace-pre-wrap font-sans text-signal-red bg-signal-red-tint border border-signal-red/25 rounded px-3 py-2">
                        {props.message.content}
                      </pre>
                      <RegenerateButton
                        onClick={handleRegenerate}
                        disabled={!props.precedingUserMessage}
                        class="self-start flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded bg-signal-red-tint text-signal-red hover:text-ink transition-colors focus-ring"
                      />
                    </div>
                  </Show>
                </Show>

                {/* Inline proposal card */}
                <Show when={props.message.proposal_id}>
                  {(proposalId) => (
                    <Show when={props.onApproveProposal}>
                      {(approveFn) => (
                        <Show when={props.onRejectProposal}>
                          {(rejectFn) => (
                            <InlineProposal
                              proposal={props.getProposal?.(proposalId())}
                              onApprove={approveFn()}
                              onReject={rejectFn()}
                              onProposalActioned={props.onProposalActioned}
                            />
                          )}
                        </Show>
                      )}
                    </Show>
                  )}
                </Show>
              </>
            }
          >
            {/* Inline edit textarea */}
            <div class="flex flex-col gap-2 min-w-[240px]">
              <textarea
                ref={editTextareaRef}
                class="w-full rounded border border-line bg-ink-2 text-paper text-sm p-2 resize-none focus-ring"
                rows={3}
                value={editText()}
                onInput={(e) => setEditText(e.currentTarget.value)}
                onKeyDown={handleEditKeyDown}
                aria-label="Edit message"
              />
              <div class="flex gap-2 justify-end">
                <button
                  type="button"
                  class="px-2 py-1 text-xs rounded bg-paper text-ink hover:bg-sunken disabled:opacity-50"
                  disabled={!editText().trim()}
                  onClick={submitEdit}
                >
                  Save
                </button>
                <button
                  type="button"
                  class="px-2 py-1 text-xs rounded bg-ink-2 text-paper/70 hover:text-paper"
                  onClick={cancelEdit}
                >
                  Cancel
                </button>
              </div>
            </div>
          </Show>
        </div>

        {/* Error indicator for failed messages */}
        <Show when={isUser() && props.message._clientError}>
          <Show when={props.onRetryMessage}>
            {(retryFn) => (
              <ErrorIndicator message={props.message} onRetry={retryFn()} />
            )}
          </Show>
        </Show>

        {/* Timestamp and optional context label */}
        <div
          class={
            "text-xs text-ink-3 mt-1 truncate " + (isUser() ? "text-right" : "")
          }
          title={formatAbsoluteTime(props.message.created_at)}
        >
          {formatRelativeTime(props.message.created_at)}
          <Show when={isUser() && props.message.context_work_item_id}>
            {(id) => (
              <span>
                {" · while viewing "}
                {id().slice(0, 8)}
              </span>
            )}
          </Show>
        </div>
      </div>

      {/* Delete confirmation modal */}
      <Modal
        open={confirmDelete()}
        onClose={() => setConfirmDelete(false)}
        title="Delete message"
        size="sm"
      >
        <p class="text-sm text-ink-2 mb-4">
          This will delete this message and all subsequent messages in the
          conversation. This cannot be undone.
        </p>
        <div class="flex gap-2 justify-end">
          <button
            type="button"
            class="px-3 py-1.5 text-sm rounded bg-surface text-ink border border-line hover:bg-sunken hover:border-line-strong"
            onClick={() => setConfirmDelete(false)}
          >
            Cancel
          </button>
          <button
            type="button"
            class="px-3 py-1.5 text-sm rounded bg-transparent text-signal-red border border-signal-red hover:bg-signal-red-tint"
            onClick={handleDelete}
          >
            Delete
          </button>
        </div>
      </Modal>

      {/* Report issue modal */}
      <ReportIssueModal
        open={reportIssueOpen()}
        onClose={() => setReportIssueOpen(false)}
        correlationId={props.message.correlation_id}
      />
    </div>
  );
};

function formatAbsoluteTime(timestamp: string): string {
  const date = new Date(timestamp);
  return date.toLocaleString([], {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatRelativeTime(timestamp: string): string {
  const date = new Date(timestamp);
  const now = Date.now();
  const diffMs = now - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHr = Math.floor(diffMin / 60);
  const diffDays = Math.floor(diffHr / 24);

  if (diffSec < 60) return "just now";
  if (diffMin < 60) return `${diffMin} min ago`;
  if (diffHr < 24) return `${diffHr} h ago`;
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return date.toLocaleDateString([], { weekday: "long" });
  return date.toLocaleDateString([], {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export default MessageThread;
